import moment from 'moment-timezone';
import { Popover } from 'antd';
import React from 'react';
import { getDayName, normalizeTZOffset } from '../../../utils/date';
import { notification } from '../../../utils/messaging';

function AvailabilityDate({
  date,
  utcDate,
  fullyBookedDates,
  limitedAvailabilityDates,
  disabledDates,
  minDate,
  maxDate,
  maxSelectableDate,
  maxSelectableDateMessage,
  selectedPackage,
  selectedBookingDates,
  clearSelectedDates,
}) {
  const limitedDate = limitedAvailabilityDates.find((ld) =>
    moment(ld.date).isSame(utcDate, 'day')
  );
  const disabledDate = disabledDates.find((d) =>
    moment(d).isSame(utcDate, 'day')
  );
  const isOutsideValidDates =
    (minDate != null && moment(utcDate).isBefore(minDate, 'day')) ||
    (maxDate != null && moment(utcDate).isAfter(maxDate, 'day'));

  const { startDate = null, endDate = null } = selectedBookingDates;
  const start = moment(normalizeTZOffset(startDate));
  const end = moment(normalizeTZOffset(endDate));

  if (selectedPackage) {
    if (isOutsideValidDates) {
      return (
        <span className={'dayUnavailable w-full h-full'}>{date.getDate()}</span>
      );
    }

    const dateDay = getDayName(date.getDay());
    const arrivalDay = selectedPackage?.arrival_day;

    const isInSelectedRange =
      maxDate != null &&
      moment(utcDate).isSameOrAfter(start, 'day') &&
      minDate != null &&
      moment(utcDate).isSameOrBefore(end, 'day');

    if (
      dateDay !== arrivalDay &&
      !isInSelectedRange &&
      selectedPackage?.arrival_day &&
      selectedPackage?.departure_day
    ) {
      return <span className={'dayBlank w-full h-full'}>{date.getDate()}</span>;
    } else if (isInSelectedRange) {
      return <span className={'w-full h-full'}>{date.getDate()}</span>;
    }
  }

  const isDepartureOnlyAndToday =
    limitedDate &&
    moment(utcDate).isSame(minDate, 'day') &&
    limitedDate.type === 'departure-only';

  const nextDay = moment(utcDate).add(1, 'day');

  const nextDayIsNotSelectable =
    limitedAvailabilityDates.find(
      (ld) =>
        moment(ld.date).isSame(nextDay, 'day') && ld.type === 'arrival-only'
    ) || fullyBookedDates.some((fd) => moment(fd).isSame(nextDay, 'day'));

  const isArrivalOnlyAndNoValidNextDate =
    limitedDate &&
    limitedDate.type === 'arrival-only' &&
    nextDayIsNotSelectable;

  const isUnavailableDate =
    isOutsideValidDates ||
    fullyBookedDates.some((fd) => moment(fd).isSame(utcDate, 'day')) ||
    isDepartureOnlyAndToday ||
    isArrivalOnlyAndNoValidNextDate;

  // Colour unavailable if it is fully-booked or in the past
  if (isUnavailableDate) {
    return (
      <span className={'dayUnavailable w-full h-full'}>{date.getDate()}</span>
    );
  }

  const isNotBookableDate =
    Boolean(maxSelectableDate) &&
    moment(utcDate).isAfter(maxSelectableDate, 'day');

  const onClick = isNotBookableDate
    ? () => {
        notification.warning({ description: maxSelectableDateMessage });
        clearSelectedDates();
      }
    : !disabledDate
      ? () => {}
      : clearSelectedDates;

  // Colour limited (with popover) if it has limited availability
  if (limitedDate && limitedDate.type !== 'no-night') {
    let text = undefined;
    if (limitedDate.type === 'arrival-only') {
      text = 'Arrival Only';
    } else if (limitedDate.type === 'departure-only') {
      text = 'Departure Only';
    }
    return (
      <Popover
        overlayClassName={'limited-availability-popover'}
        content={<span className={'txt-underline gRegular fs-12'}>{text}</span>}
        trigger={text ? ['hover', 'click'] : 'none'}
      >
        <span className={'dayLimited w-full h-full'} onClick={onClick}>
          {date.getDate()}
        </span>
      </Popover>
    );
  }

  // No night session indicator
  if (limitedDate && limitedDate.type === 'no-night') {
    const nextDay = moment(utcDate).add(1, 'day');
    const isNextDayUnavailable =
      (minDate != null && nextDay.isBefore(minDate, 'day')) ||
      (maxDate != null && nextDay.isAfter(maxDate, 'day')) ||
      fullyBookedDates.some((fd) => moment(fd).isSame(nextDay, 'day'));
    const nextDayLimited = limitedAvailabilityDates.find(
      (ld) => moment(ld.date).isSame(nextDay, 'day') && ld.type !== 'no-night'
    );
    // Only show no-night indicator if the next day is green (or another no-night)
    if (isNextDayUnavailable || nextDayLimited) {
      return (
        <span className={'w-full h-full'} onClick={onClick}>
          {date.getDate()}
        </span>
      );
    }
    return (
      <span className={'dayNoNight w-full h-full'} onClick={onClick}>
        {date.getDate()}
      </span>
    );
  }

  // Otherwise colour available
  return (
    <span className={'w-full h-full'} onClick={onClick}>
      {date.getDate()}
    </span>
  );
}

export default AvailabilityDate;
